// @ftedn/shared — Feature flag resolver (pure function)
// Resolves a flag value across 4 layers: env → cohort → user → mechanic config.
// Highest specificity wins. Type-safe with validation.

import { FLAG_DEFINITIONS, type FlagName, type FlagDef } from './definitions.js';

export interface FlagLayers {
  /** Environment-level defaults (from env vars / flag-defaults.ts) */
  env?: Record<string, unknown>;
  /** Cohort-level overrides (merged from all user cohorts) */
  cohort?: Record<string, unknown>;
  /** Per-user overrides */
  user?: Record<string, unknown>;
  /** Per-mechanic runtime config (e.g. from appConfig) */
  mechanic?: Record<string, unknown>;
}

/**
 * Resolve a flag value from the 4-layer stack.
 * Resolution order (highest wins): mechanic → user → cohort → env → definition default
 */
export function resolveFlag<N extends FlagName>(
  name: N,
  layers: FlagLayers
): unknown {
  const def = FLAG_DEFINITIONS[name] as FlagDef | undefined;
  if (!def) return undefined;

  // Check layers from highest specificity to lowest
  const raw = pickFirstDefined(name, [
    layers.mechanic,
    layers.user,
    layers.cohort,
    layers.env,
  ]);

  if (raw === undefined) return def.default;

  return coerceAndValidate(raw, def);
}

/**
 * Resolve all flags at once for a given layer stack.
 * Returns a Record<FlagName, value> with every flag resolved.
 */
export function resolveAllFlags(layers: FlagLayers): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const name of Object.keys(FLAG_DEFINITIONS) as FlagName[]) {
    result[name] = resolveFlag(name, layers);
  }

  return result;
}

/**
 * Validate a raw value against a flag definition.
 * Returns { valid, coerced } — coerced is the type-safe value.
 */
export function validateFlagValue(
  def: FlagDef, value: unknown
): { valid: boolean; coerced: unknown; error?: string } {
  try {
    const coerced = coerceAndValidate(value, def);
    return { valid: true, coerced };
  } catch (e) {
    return { valid: false, coerced: def.default, error: (e as Error).message };
  }
}

// ── Internal helpers ────────────────────────────────────

function pickFirstDefined(
  key: string,
  layers: (Record<string, unknown> | undefined)[]
): unknown {
  for (const layer of layers) {
    const value = readLayerValue(layer, key);
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function readLayerValue(layer: Record<string, unknown> | undefined, key: string): unknown {
  if (!layer) return undefined;
  if (key in layer && layer[key] !== undefined) return layer[key];

  let cursor: unknown = layer;
  for (const part of key.split('.')) {
    if (!cursor || typeof cursor !== 'object' || !(part in cursor)) {
      return undefined;
    }
    cursor = (cursor as Record<string, unknown>)[part];
  }

  return cursor;
}

function coerceAndValidate(raw: unknown, def: FlagDef): unknown {
  switch (def.type) {
    case 'bool':
      return coerceBool(raw);

    case 'int': {
      const n = coerceInt(raw);
      if (n < def.min) return def.min;
      if (n > def.max) return def.max;
      return n;
    }

    case 'enum': {
      const s = String(raw);
      if (!def.options.includes(s)) {
        throw new Error(
          `Invalid enum value "${s}"; must be one of: ${def.options.join(', ')}`
        );
      }
      return s;
    }

    default:
      return raw;
  }
}

function coerceBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true' || value === '1') return true;
    if (value === 'false' || value === '0') return false;
  }
  if (typeof value === 'number') return value !== 0;
  return Boolean(value);
}

function coerceInt(value: unknown): number {
  if (typeof value === 'number') return Math.round(value);
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  throw new Error(`Cannot coerce "${value}" to int`);
}
